import type { Rating as RatingType } from "@homeinstead/schemas";
import type {
  CareSeekerShallowLeadFormData,
  ShallowCareOption,
  ShallowCareOptionSelection,
} from "~care/types/forms/care-seeker-shallow-lead-form";

import { cn } from "@homeinstead/ui";
import React, { Suspense } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { ServiceTypeTrackedClickEvent } from "~care/types/events";
import { Rating } from "../../Rating/Rating";
import { CheckboxButton } from "../Checkbox";
import ContinueButton from "./ContinueButton";
import { CareOptions } from "./form-options";

const CareOptionStep = ({
  nextStep,
  className,
  franchiseRating,
}: {
  nextStep: () => void;
  className?: string;
  franchiseRating?: RatingType;
}) => {
  const { rating, ratingCount, ratingService } = franchiseRating ?? {};

  const { watch, control } = useFormContext<CareSeekerShallowLeadFormData>();

  const step3Values = watch("step3");

  const isEnabled = Object.values(step3Values).some((val) => val === true);

  const trackingEvent = {
    eventName: "tracked_click",
    eventPayloadGenerator: (path: string, url: string) => {
      const selectedOptions = Object.entries(step3Values)
        .filter(([, value]) => value === true)
        .map(([key]) => key as ShallowCareOption);

      return ServiceTypeTrackedClickEvent(
        selectedOptions as ShallowCareOptionSelection,
        path,
        url,
      );
    },
  };

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4 gap-y-12 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex flex-col gap-4 rounded-lg xl:col-start-1 xl:row-start-1">
        <div>
          <h1 className="mb-6 text-center font-editorial-new font-light text-1xl xl:text-left">
            What can we help with?
          </h1>
        </div>
        <div className="flex flex-col items-center gap-4 md:max-w-[440px] xl:items-start">
          {CareOptions.map(([fieldName, fieldLabel]) => {
            const checkboxPath = `step3.${fieldName}` as const;
            return (
              <Controller
                control={control}
                key={fieldName}
                name={checkboxPath}
                render={({ field }) => (
                  <div className="flex w-full items-center">
                    <CheckboxButton
                      buttonSize="short"
                      checked={field.value}
                      containerClassName="md:w-full"
                      id={fieldName}
                      name={field.name}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {fieldLabel}
                      </div>
                    </CheckboxButton>
                  </div>
                )}
              />
            );
          })}
          <p className="w-full py-2 text-new-beginnings-base">
            If you don&apos;t see a specific type of care here, you will be able
            to provide additional details once we match you with a local office.{" "}
          </p>
        </div>
        <div className="md:max-w-[440px]">
          <Suspense>
            <ContinueButton
              isEnabled={isEnabled}
              nextStep={nextStep}
              trackingEvent={trackingEvent}
            />
          </Suspense>
        </div>
      </div>

      <div className="mx-auto mb-auto flex max-w-md flex-col gap-6 xl:mr-0 xl:ml-auto">
        <div className="mt-8 flex justify-center xl:mt-0 xl:justify-end">
          {rating !== undefined && (ratingCount ?? 0) > 0 && (
            <Rating
              className="self-center xl:mt-3"
              rating={rating}
              ratingCount={ratingCount!}
              service={ratingService}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CareOptionStep;
